import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import { StyledTextField, SimpleModal } from '@labradorsports/components';
import { useSite, AuthenticationErrors } from '@labradorsports/utils';

import { useDispatcher } from '../../shared/hooks/index.js';
import { addValidation, useValidation } from '../../shared/providers/index.js';
import { HelmetWrapper } from '../../shared/structural/index.js';
import {
    Selectors,
    useSendPasswordResetMutation,
    LoginPopoverForms,
    useDeleteGlobalSuppressionMutation,
    authActions,
} from '../../store/index.js';

interface Props {
    changeForm?: (form?: string) => any;
}

const ForgotPasswordForm: FC<Props> = ({ changeForm }) => {
    const { Config } = useSite();
    const [sendPasswordReset] = useSendPasswordResetMutation();
    const [deleteGlobalSuppression] = useDeleteGlobalSuppressionMutation();
    const setLoginEmail = useDispatcher(authActions.SetLoginEmail);
    const accountFormError = useSelector(Selectors.accountFormError);
    const email = useSelector(Selectors.loginEmail);
    const [showSuppressionModal, setShowSuppressionModal] = useState(false);

    const { errors, submitted, setSubmitted, validateRef } = useValidation(
        () => ({
            email: !email,
        }),
        [email]
    );

    const emailChange = (event: any) => {
        setLoginEmail(event.target.value);
    };

    const confirmSendPasswordReset = async () => {
        if (await validateRef.current()) {
            setSubmitted(false);

            sendPasswordReset({ email }).then((result) => {
                if ('error' in result) {
                    // Check if it's a global suppression error using error.is()
                    if (result.error.code === AuthenticationErrors.EMAIL_GLOBALLY_SUPPRESSED.code) {
                        setShowSuppressionModal(true);
                    }
                } else {
                    changeForm();
                }
            });
        } else {
            setSubmitted(true);
        }
    };

    const keyPress = (event: any) => {
        if (event.key === 'Enter') {
            confirmSendPasswordReset();
        }
    };

    const showLogin = () => {
        changeForm(LoginPopoverForms.LOGIN);
    };

    const handleRemoveGlobalSuppression = async () => {
        await deleteGlobalSuppression({ email });
        setShowSuppressionModal(false);
        // Now proceed with password reset
        sendPasswordReset({ email }).then((result) => {
            if (!('error' in result)) {
                changeForm();
            }
        });
    };

    const canonical = `${Config.SiteConfig.Url}/forgot-password`;

    const globalSuppressionButtons = [
        {
            text: 'Cancel',
            action: () => setShowSuppressionModal(false),
        },
        {
            text: 'Re-enable Email Notifications',
            action: handleRemoveGlobalSuppression,
            variant: 'contained',
            color: 'primary',
        },
    ];

    return (
        <div className="signup-form">
            <HelmetWrapper title="Forgot Password">
                <link rel="canonical" href={canonical} />
                <meta property="og:url" content={canonical} />
            </HelmetWrapper>
            <h2>Forgot Your Password?</h2>
            <p>Enter your email below, and we will send you an email to reset your password and create a new one.</p>
            <StyledTextField
                value={email}
                onChange={emailChange}
                label="Email"
                onKeyPress={keyPress}
                error={submitted && errors.email}
                helperText={submitted && errors.email && 'Please enter an email address'}
                fullWidth
            />
            {accountFormError ? <span className="error-text">{accountFormError.message}</span> : undefined}
            <div className="row">
                <div className="col text-start">
                    <div className="small">Remember your password?</div>
                    <Button tabIndex={1} onClick={showLogin}>
                        Log In
                    </Button>
                </div>
                <div className="col-auto text-end">
                    <Button variant="contained" onClick={confirmSendPasswordReset} color="secondary">
                        Send Email
                    </Button>
                </div>
            </div>

            <SimpleModal
                open={showSuppressionModal}
                onClose={() => setShowSuppressionModal(false)}
                title="Email Notifications Disabled"
                text="We found your email address in our global suppression list. This means you will not receive any email notifications, including password reset emails. To receive password reset emails, you'll need to re-enable email notifications first."
                buttons={globalSuppressionButtons}
            />
        </div>
    );
};

export default addValidation(ForgotPasswordForm);
